import React, { useCallback, useRef, type ReactNode } from 'react';
import c from 'classnames';
import isFunction from 'lodash/isFunction';
import ddt from '@wix/da-ddt';
import RemovableTag from '../../../TagList/RemovableTag';
import AutoCompleteLayout from '../../layouts/AutoCompleteLayout';
import BaseInput, { BaseInputProps } from '../BaseInput';
import InputFieldOutline from '../InputFieldOutline';
import { isNewTag, parseTextIntoTags } from './helpers';

import s from './TagInputField.scss';

const logger = ddt.createLogger('TagInputField');

export interface Props extends BaseInputProps {
  id: string;
  value: string;
  tags: string[];
  tagSuggestions: string[];
  validationResult?: 'success' | 'error';
  valueAsFirstSuggestion?: boolean;
  isTruncated?: boolean;
  fieldClassname?: string;
  postfix?: React.ReactNode;
  /** Override the default paste parsing. Receives raw pasted text and existing tags; returns tags to add. */
  parseText?: (text: string, tags: string[]) => string[];

  onAddTag: (tag: string) => void;
  onAddTags?: (tag: string[]) => void;
  onRemoveTag: (tag: string) => void;
  onCancel: () => void;
  renderTag?: (tag: string) => ReactNode;
}

const TagInputField = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      tags = [],
      tagSuggestions,
      fieldClassname,
      onAddTag,
      onAddTags,
      onRemoveTag,
      onKeyDown,
      onBeforeInput,
      onCancel,
      value,
      validationResult,
      placeholder,
      isTruncated,
      postfix,
      parseText,
      // TODO: Handle `isRequired`.
      // Not sure how to structure this as of writting since I don't know if this will ever
      // be applicable
      isRequired,
      disabled,
      valueAsFirstSuggestion,
      renderTag,
      ...rest
    },
    ref
  ) => {
    const localInputRef = useRef<HTMLInputElement | null>(null);
    const clickIsTag = useRef<boolean>(false);

    // Wrapped to prevent duplicate tags being submitted
    const handleAddTag = useCallback(
      tag => {
        if (isNewTag(tags, tag)) {
          onAddTag(tag);
        }
      },
      [onAddTag, tags]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
          case 'Backspace':
            if (value === '' && tags.length > 0) {
              onRemoveTag(tags[tags.length - 1]);
              e.preventDefault();
              e.stopPropagation();
            }
            break;
          case 'Escape':
            onCancel();
            e.preventDefault();
            e.stopPropagation();
            break;
          default:
            break;
        }

        onKeyDown && onKeyDown(e);
      },
      [value, onRemoveTag, onKeyDown, onCancel, tags]
    );

    /**
     * We have to parse content input keys separately from the special keys above because
     * Chrome on android fires an onKeyDown event with undefined key data as per the W3 spec.
     *
     * The beforeInput event is more reliable cross-platform.
     */
    const handleBeforeInput = useCallback(
      (e: React.InputEvent<HTMLInputElement>) => {
        switch (e.data) {
          case ',':
          case ' ':
          case 'Space':
            // Prevent empty tags being added
            if (value.length > 0 && !e.nativeEvent.isComposing) {
              handleAddTag(value);
            }
            if (!e.nativeEvent.isComposing) {
              e.preventDefault();
            }
            e.stopPropagation();
            break;
          default:
            break;
        }

        onBeforeInput && onBeforeInput(e);
      },
      [value, handleAddTag, onBeforeInput]
    );

    const handleAutoCompleteSelect = useCallback(
      autoCompleteItem => {
        handleAddTag(autoCompleteItem.value);
        localInputRef.current?.focus();
      },
      [handleAddTag]
    );

    // Add input as first suggestion
    const freeformSuggestion: any[] = [];
    if (valueAsFirstSuggestion && value && value.trim() !== '') {
      freeformSuggestion.push({ label: value, value });
    }

    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        e.preventDefault();
        try {
          const text = e.clipboardData.getData('text');
          const parsed = parseText ? parseText(text, tags) : parseTextIntoTags(text, tags);
          onAddTags && onAddTags(parsed);
        } catch (err) {
          logger.error(`add tags failed: ${err}`);
        }
      },
      [onAddTags, tags, parseText]
    );

    return (
      <AutoCompleteLayout
        items={[
          ...freeformSuggestion,
          ...tagSuggestions.map(suggestion => ({
            label: suggestion,
            value: suggestion,
          })),
        ]}
        onSelect={handleAutoCompleteSelect}
        onKeyDown={handleKeyDown}
        menuId={`autocomplete-${id}`}
        className={s['autocomplete-layout']}
      >
        <InputFieldOutline
          className={c(s['outline-root'], fieldClassname)}
          outlineStyle={disabled ? 'disabled' : validationResult}
          onMouseDown={(e: any) => {
            if (e.target && e.target.getAttribute && e.target.getAttribute('data-tag')) {
              clickIsTag.current = true;
            } else {
              clickIsTag.current = false;
            }
          }}
          onMouseUp={e => {
            if (!clickIsTag.current) {
              localInputRef.current && localInputRef.current.focus();
            }
          }}
        >
          <div className={c(s['content-wrapper'], postfix !== undefined && s['with-postfix'])}>
            {tags.map((tag, index) => (
              <RemovableTag
                data-tag="1"
                size="small"
                key={`tag-${index}`}
                className={s['tag']}
                onClick={() => onRemoveTag(tag)}
                isTruncated={isTruncated}
                isFakeButton
              >
                {renderTag ? renderTag(tag) : tag}
              </RemovableTag>
            ))}

            <BaseInput
              id={id}
              disabled={disabled}
              value={value}
              className={s['input']}
              placeholder={tags.length === 0 ? placeholder : undefined}
              // We're providing our own autocomplete
              autoComplete="off"
              ref={node => {
                if (isFunction(ref)) {
                  ref(node);
                } else if (ref) {
                  ref.current = node;
                }
                localInputRef.current = node;
              }}
              onPaste={handlePaste}
              onBeforeInput={handleBeforeInput}
              {...rest}
            />
            {postfix && <span className={s['postfix']}>{postfix}</span>}
          </div>
        </InputFieldOutline>
      </AutoCompleteLayout>
    );
  }
);
TagInputField.displayName = 'TagInputField';

export default TagInputField;
